/**
 * Phase 8 T-26 §29.E — Multi-JD intake body.
 *
 * Mounted inside the intake shell on the unified `'job'` tab once the
 * user has queued 1+ JDs (21-Apr unification). Renders:
 *   - A 480 × 240 drag-drop zone with `[+ Paste JD]` + `[+ Upload file]` chips.
 *   - A list of added JDs below (80 px cards): `JD {N}` + first 140 chars
 *     + `[×]` remove + `[⋯]` menu (edit / duplicate).
 *   - A `[⚙ Manual review]` toggle in the toolbar area. Toggling it flips
 *     the thread's `manualReview` flag so Stage 1 renders the accordion
 *     (§29.E).
 *
 * Caps:
 *   - Max 10 JDs per run. 11th triggers the §23.B warning toast.
 *
 * The body OWNS the JD list state. On Submit, the intake page lifts the
 * current list (`getJds()`) + the manual-review flag out of here and
 * persists them in the new thread.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FileUp, MoreHorizontal, Paperclip, Plus, Settings, X } from 'lucide-react';
import { useGlobalToast } from '@shared/components/Toast/ToastProvider';

export const MAX_JDS = 10;

export interface MultiJdItem {
  id: string;
  /** First 140 chars of the JD text — used as the card preview. */
  preview: string;
  /** The raw pasted / uploaded JD body. */
  text: string;
  /** Optional title (file name or derived) for display. */
  title: string;
}

export interface MultiJdListHandle {
  getJds: () => MultiJdItem[];
  isManualReview: () => boolean;
}

interface MultiJdListProps {
  /** Seed list (used when re-hydrating from a draft). */
  initial?: MultiJdItem[];
  initialManualReview?: boolean;
  /** Parent receives every mutation so it can persist a draft per-tab. */
  onChange?: (jds: MultiJdItem[], manualReview: boolean) => void;
  /** Imperative handle — intake page reads the final list on Submit. */
  handleRef?: React.MutableRefObject<MultiJdListHandle | null>;
}

function newJdId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `jd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function preview140(text: string): string {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  return trimmed.length <= 140 ? trimmed : `${trimmed.slice(0, 139)}…`;
}

export function MultiJdList({
  initial = [],
  initialManualReview = false,
  onChange,
  handleRef,
}: MultiJdListProps) {
  const { showToast } = useGlobalToast();
  const [jds, setJds] = useState<MultiJdItem[]>(initial);
  const [manualReview, setManualReview] = useState(initialManualReview);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pasteTextareaId = useId();

  // Propagate every mutation upstream.
  useEffect(() => {
    onChange?.(jds, manualReview);
  }, [jds, manualReview, onChange]);

  // Expose imperative handle.
  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      getJds: () => jds,
      isManualReview: () => manualReview,
    };
    return () => {
      if (handleRef.current) handleRef.current = null;
    };
  }, [handleRef, jds, manualReview]);

  const warn = useCallback(
    (title: string) => {
      showToast({
        title,
        body: `You have ${jds.length} of ${MAX_JDS} JDs already. Remove one before adding more.`,
        variant: 'warning',
      });
    },
    [showToast, jds.length],
  );

  const addJd = useCallback(
    (text: string, title?: string) => {
      const trimmed = text.trim();
      if (!trimmed) return false;
      if (jds.length >= MAX_JDS) {
        warn('Max 10 JDs per run.');
        return false;
      }
      const item: MultiJdItem = {
        id: newJdId(),
        text: trimmed,
        preview: preview140(trimmed),
        title: title ?? `JD ${jds.length + 1}`,
      };
      setJds((prev) => [...prev, item]);
      return true;
    },
    [jds.length, warn],
  );

  const addManyTexts = useCallback(
    (entries: Array<{ text: string; title?: string }>) => {
      let nextCount = jds.length;
      const rejected: string[] = [];
      const accepted: MultiJdItem[] = [];
      for (const { text, title } of entries) {
        const trimmed = text.trim();
        if (!trimmed) continue;
        if (nextCount >= MAX_JDS) {
          rejected.push(title ?? 'untitled');
          continue;
        }
        accepted.push({
          id: newJdId(),
          text: trimmed,
          preview: preview140(trimmed),
          title: title ?? `JD ${nextCount + 1}`,
        });
        nextCount += 1;
      }
      if (accepted.length > 0) {
        setJds((prev) => [...prev, ...accepted]);
      }
      if (rejected.length > 0) {
        warn('Max 10 JDs per run.');
      }
    },
    [jds.length, warn],
  );

  const removeJd = useCallback((id: string) => {
    setJds((prev) => prev.filter((j) => j.id !== id));
    setMenuOpenId(null);
  }, []);

  const duplicateJd = useCallback(
    (id: string) => {
      setMenuOpenId(null);
      if (jds.length >= MAX_JDS) {
        warn('Max 10 JDs per run.');
        return;
      }
      const src = jds.find((j) => j.id === id);
      if (!src) return;
      const copy: MultiJdItem = {
        id: newJdId(),
        text: src.text,
        preview: src.preview,
        title: `${src.title} (copy)`,
      };
      setJds((prev) => [...prev, copy]);
    },
    [jds, warn],
  );

  const beginEdit = useCallback(
    (id: string) => {
      setMenuOpenId(null);
      const src = jds.find((j) => j.id === id);
      if (!src) return;
      setEditingId(id);
      setEditText(src.text);
    },
    [jds],
  );

  const commitEdit = useCallback(() => {
    const trimmed = editText.trim();
    if (!editingId) return;
    if (!trimmed) {
      setEditingId(null);
      setEditText('');
      return;
    }
    setJds((prev) =>
      prev.map((j) =>
        j.id === editingId
          ? { ...j, text: trimmed, preview: preview140(trimmed) }
          : j,
      ),
    );
    setEditingId(null);
    setEditText('');
  }, [editingId, editText]);

  const commitPaste = useCallback(() => {
    const ok = addJd(pasteText, `JD ${jds.length + 1}`);
    if (ok) {
      setPasteOpen(false);
      setPasteText('');
    }
  }, [addJd, pasteText, jds.length]);

  const handleFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      const entries: Array<{ text: string; title: string }> = [];
      for (const f of files) {
        const name = f.name;
        const ext = name.split('.').pop()?.toLowerCase() ?? '';
        try {
          if (ext === 'txt' || f.type.startsWith('text/')) {
            const text = await f.text();
            entries.push({ text, title: name });
          } else if (ext === 'md') {
            const text = await f.text();
            entries.push({ text, title: name });
          } else {
            // For non-text JD uploads, we just keep the file name as a
            // placeholder. Server ingestion swaps the body for parsed
            // text; until then we display the file name with a clear
            // indicator that the body will be parsed at scoring time.
            entries.push({
              text: `(Uploaded ${name} — full text will be parsed at scoring time.)`,
              title: name,
            });
          }
        } catch {
          entries.push({
            text: `(Failed to read ${name} client-side.)`,
            title: name,
          });
        }
      }
      addManyTexts(entries);
    },
    [addManyTexts],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (!e.dataTransfer?.files) return;
      void handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const atCap = jds.length >= MAX_JDS;
  const countLabel = useMemo(
    () => `${jds.length} / ${MAX_JDS} JDs`,
    [jds.length],
  );

  return (
    <div className="w-full max-w-[640px]">
      {/* Top toolbar — count + [⚙ Manual review] */}
      <div className="mb-3 flex items-center justify-between">
        <span
          className="text-[12px] font-medium"
          style={{
            color: atCap
              ? 'var(--vscode-text-secondary)'
              : 'var(--vscode-text-tertiary)',
          }}
        >
          {countLabel}
        </span>
        <label
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-[10px] border px-2.5 py-1 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{
            borderColor: manualReview ? '#10B981' : 'var(--vscode-border-primary)',
            color: manualReview ? '#065F46' : 'var(--vscode-text-secondary)',
            background: manualReview ? 'var(--ggj-accent-soft,#D1FAE5)' : 'transparent',
          }}
        >
          <input
            type="checkbox"
            checked={manualReview}
            onChange={(e) => setManualReview(e.target.checked)}
            className="sr-only"
          />
          <Settings className="h-3.5 w-3.5" aria-hidden />
          Manual review
        </label>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className="flex flex-col items-center justify-center gap-3 rounded-[12px] border-2 border-dashed px-6 py-8 text-center transition-colors"
        style={{
          width: '100%',
          maxWidth: 480,
          minHeight: 240,
          borderColor: dragging ? '#10B981' : 'var(--vscode-border-primary)',
          background: dragging ? 'var(--ggj-accent-soft,#D1FAE5)' : 'var(--vscode-bg-elevated)',
        }}
        role="region"
        aria-label="JD upload drop zone"
      >
        <p
          className="text-[13px]"
          style={{ color: 'var(--vscode-text-secondary)' }}
        >
          Drop multiple JDs here, or add them one at a time.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (atCap) {
                warn('Max 10 JDs per run.');
                return;
              }
              setPasteOpen(true);
            }}
            className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          >
            <Paperclip className="h-3.5 w-3.5" aria-hidden />
            Paste JD
          </button>
          <button
            type="button"
            onClick={() => {
              if (atCap) {
                warn('Max 10 JDs per run.');
                return;
              }
              fileInputRef.current?.click();
            }}
            className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          >
            <FileUp className="h-3.5 w-3.5" aria-hidden />
            Upload file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".txt,.md,.pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => {
              if (!e.target.files) return;
              void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {/* Paste overlay */}
      {pasteOpen ? (
        <div
          className="mt-3 rounded-[10px] border p-3"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
          role="dialog"
          aria-label="Paste JD text"
        >
          <label
            htmlFor={pasteTextareaId}
            className="mb-1 block text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            Paste JD text
          </label>
          <textarea
            id={pasteTextareaId}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={4}
            placeholder="Paste the JD here…"
            className="min-h-[96px] w-full resize-none rounded-[8px] border bg-transparent p-2 text-[13px] outline-none focus:ring-1 focus:ring-[var(--ggj-accent,#10B981)]"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
            }}
            autoFocus
          />
          <div className="mt-2 flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={() => {
                setPasteOpen(false);
                setPasteText('');
              }}
              className="inline-flex h-7 items-center rounded-[8px] px-2.5 text-[12px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={commitPaste}
              disabled={!pasteText.trim()}
              className="inline-flex h-7 items-center gap-1 rounded-[8px] bg-emerald-500 px-2.5 text-[12px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-3 w-3" aria-hidden />
              Add JD
            </button>
          </div>
        </div>
      ) : null}

      {/* JD card list */}
      {jds.length > 0 ? (
        <ul
          className="mt-4 flex flex-col gap-2"
          role="list"
          aria-label="Added job descriptions"
        >
          {jds.map((j, idx) => {
            const isEditing = editingId === j.id;
            return (
              <li
                key={j.id}
                className="relative flex items-start gap-3 rounded-[10px] border px-3 py-2"
                style={{
                  height: isEditing ? 'auto' : 80,
                  background: 'var(--vscode-bg-elevated)',
                  borderColor: 'var(--vscode-border-primary)',
                }}
              >
                <div
                  className="flex h-8 w-10 shrink-0 items-center justify-center rounded-[8px] text-[11px] font-semibold"
                  style={{
                    background: 'var(--ggj-accent-soft,#D1FAE5)',
                    color: '#065F46',
                  }}
                >
                  JD {idx + 1}
                </div>
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={4}
                      className="min-h-[88px] w-full resize-y rounded-[6px] border bg-transparent p-2 text-[12px] outline-none focus:ring-1 focus:ring-[var(--ggj-accent,#10B981)]"
                      style={{
                        borderColor: 'var(--vscode-border-primary)',
                        color: 'var(--vscode-text-primary)',
                      }}
                      autoFocus
                      aria-label="Edit JD text"
                    />
                  ) : (
                    <>
                      <p
                        className="truncate text-[12px] font-medium"
                        style={{ color: 'var(--vscode-text-primary)' }}
                        title={j.title}
                      >
                        {j.title}
                      </p>
                      <p
                        className="line-clamp-2 text-[12px]"
                        style={{ color: 'var(--vscode-text-secondary)' }}
                      >
                        {j.preview}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={commitEdit}
                        className="inline-flex h-7 items-center rounded-[8px] bg-emerald-500 px-2 text-[11px] font-medium text-white"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setEditText('');
                        }}
                        className="inline-flex h-7 items-center rounded-[8px] px-2 text-[11px]"
                        style={{ color: 'var(--vscode-text-tertiary)' }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setMenuOpenId((prev) => (prev === j.id ? null : j.id))
                        }
                        className="rounded p-1 transition-colors hover:bg-[var(--vscode-bg-hover)]"
                        aria-label={`Options for JD ${idx + 1}`}
                        aria-haspopup="menu"
                        aria-expanded={menuOpenId === j.id}
                        style={{ color: 'var(--vscode-text-tertiary)' }}
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeJd(j.id)}
                        className="rounded p-1 transition-colors hover:bg-[var(--vscode-bg-hover)]"
                        aria-label={`Remove JD ${idx + 1}`}
                        style={{ color: 'var(--vscode-text-tertiary)' }}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </>
                  )}
                </div>

                {menuOpenId === j.id ? (
                  <>
                    <div
                      className="fixed inset-0 z-[140]"
                      onClick={() => setMenuOpenId(null)}
                      aria-hidden
                    />
                    <div
                      role="menu"
                      className="absolute right-2 top-8 z-[150] w-[140px] rounded-[8px] border py-1 shadow-lg"
                      style={{
                        background: 'var(--vscode-bg-elevated)',
                        borderColor: 'var(--vscode-border-primary)',
                      }}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => beginEdit(j.id)}
                        className="block w-full px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                        style={{ color: 'var(--vscode-text-primary)' }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => duplicateJd(j.id)}
                        className="block w-full px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                        style={{ color: 'var(--vscode-text-primary)' }}
                      >
                        Duplicate
                      </button>
                    </div>
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
